/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'clsx';
import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { TableCell, TableRow, Typography } from '@mui/material';

import { Namespaces } from '../../../../../../../../i18n';
import { useTableStyles } from '../../../../../../../../styles/table';
import { DatasetImport } from '../../../../../../../../types/dataset/DatasetImport';
import { DatasetImportsTableContext } from '../../DatasetImportsTable.context';
import { DatasetImportsTableDatabaseHeading } from '../DatasetImportsTableDatabaseHeading/DatasetImportsTableDatabaseHeading';
import { DatasetImportsTableImport } from '../DatasetImportsTableImport/DatasetImportsTableImport';

type DatasetImportsTableDatabaseProps = {
  databaseName: string;
  datasetImports: DatasetImport[];
};

export function DatasetImportsTableDatabase(props: DatasetImportsTableDatabaseProps) {
  const { databaseName, datasetImports } = props;
  const tableClasses = useTableStyles();
  const { t } = useTranslation(Namespaces.Cluster);

  const { toggleDatabaseCollapsed, isCollapsedDatabase, selectedDatabase } = useContext(DatasetImportsTableContext);

  const collapsed = useMemo<boolean>(
    () => selectedDatabase !== databaseName && isCollapsedDatabase(databaseName),
    [databaseName, isCollapsedDatabase, selectedDatabase],
  );

  return (
    <>
      <DatasetImportsTableDatabaseHeading
        databaseName={databaseName}
        onCollapseIconClick={() => toggleDatabaseCollapsed(databaseName)}
        collapsed={collapsed}
        showCollapseBtn={!selectedDatabase}
      />
      {!collapsed && datasetImports.length > 0 ? (
        datasetImports.map(datasetImport => (
          <DatasetImportsTableImport key={datasetImport.id} datasetImport={datasetImport} databaseName={databaseName} />
        ))
      ) : (
        <TableRow>
          <TableCell className={cx(tableClasses.bodyTableCell, tableClasses.smallCell)} colSpan={99}>
            <Typography component="p" className={tableClasses.bodyTableCellText}>
              {t('details.datasets.dataset.no_imports_in_database')}
            </Typography>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
